import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Copy, Check, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function formatCommission(type, val) {
  if (type === "percent") return `${val}%`;
  if (type === "flat") return `$${val}`;
  if (type === "tiered") return "Tiered";
  return "—";
}

export default function AffiliateLinks() {
  const [links, setLinks] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => { api.get("/affiliate/links").then((r) => setLinks(r.data)); }, []);

  const copy = (cid, url) => {
    navigator.clipboard.writeText(`${origin}${url}`);
    setCopiedId(cid);
    toast.success("Link copied");
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div data-testid="affiliate-links">
      <div className="pf-chip pf-chip-lav">Share &amp; earn</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Tracking links</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Every click on these links is tracked, and every completed order earns you commission. Copy a link and share it in your content.
      </p>

      <div className="mt-6 space-y-3">
        {links.length === 0 && (
          <div className="pf-card p-8 text-center text-slate-500">
            <Link2 className="w-6 h-6 mx-auto mb-2 text-cyan-600" />
            No campaigns are running affiliates yet. Check back soon.
          </div>
        )}
        {links.map((l) => {
          const fullUrl = `${origin}${l.tracker_url}`;
          const copied = copiedId === l.campaign_id;
          return (
            <div key={l.campaign_id} className="pf-card p-5 grid md:grid-cols-[64px_1fr_auto] items-center gap-4" data-testid={`aff-link-${l.campaign_id}`}>
              {l.store_logo && <img src={l.store_logo} alt={l.store_name} className="w-16 h-16 rounded-2xl border border-white/80 bg-white object-contain" data-logo="1" />}
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{l.store_name}</div>
                <div className="font-display text-lg mt-1 truncate">{l.campaign_name}</div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="pf-chip pf-chip-mint">{formatCommission(l.commission_type, l.commission_value)} commission</span>
                  <span className="pf-chip">
                    {l.discount_type === "percent" && `${l.discount_value}% off`}
                    {l.discount_type === "fixed" && `$${l.discount_value} off`}
                    {l.discount_type === "shipping" && "Free shipping"}
                    {l.discount_type === "bogo" && "BOGO"}
                  </span>
                </div>
                <code className="mt-3 block font-mono text-xs bg-white/70 rounded-md px-3 py-2 truncate">{fullUrl}</code>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => copy(l.campaign_id, l.tracker_url)} className="pf-btn-primary px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid={`aff-copy-${l.campaign_id}`}>
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copied" : "Copy link"}
                </button>
                <a href={l.tracker_url} target="_blank" rel="noreferrer" className="pf-btn-glass px-3 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid={`aff-open-${l.campaign_id}`}>
                  <ExternalLink className="w-3 h-3" /> Preview
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
